import { notificationTitle } from '@kanos/domain';
import { Button, buttonClassName, EmptyNote, SegmentedControl, StatusBadge } from '@kanos/ui';
import { formatDateTime, Page, ProblemAlert, QueryContent } from '@kanos/web-core';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { BellOff, Check, CheckCheck } from 'lucide-react';
import { z } from 'zod';
import {
  notificationsQuery,
  useReadNotifications,
  type Notification,
} from '../firm/notification-queries';
import { useMe } from '../me';
import { messageTarget, opensIntake } from '../firm/message-target';

/** `?view=unread`: the unread only. */
const Search = z.object({ view: z.literal('unread').optional().catch(undefined) });

export const Route = createFileRoute('/inbox')({
  validateSearch: Search,
  component: Inbox,
});

const VIEWS = [
  { id: 'all', label: 'Alle' },
  { id: 'unread', label: 'Ungelesen' },
] as const;

/**
 * The person's own messages (ADR-042, after Corly's inbox): what happened for them, newest
 * first - opened, it counts as read. Gone after 90 days; the audit log keeps what happened.
 */
function Inbox() {
  const me = useMe();
  const { view } = Route.useSearch();
  const navigate = Route.useNavigate();
  const unread = view === 'unread';
  const messages = useQuery(notificationsQuery(me.activeTenantId, unread));
  const read = useReadNotifications(me.activeTenantId);
  const count = messages.data?.unread ?? 0;
  return (
    <Page
      title="Mitteilungen"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <SegmentedControl
            aria-label="Ansicht"
            size="sm"
            options={VIEWS}
            value={unread ? 'unread' : 'all'}
            onChange={(next) =>
              void navigate({ search: next === 'unread' ? { view: 'unread' } : {}, replace: true })
            }
          />
          <Button
            variant="secondary"
            size="sm"
            isDisabled={count === 0}
            isPending={read.isPending && read.variables === null}
            onPress={() => read.mutate(null)}
          >
            <CheckCheck aria-hidden className="size-4" />
            Alle als gelesen markieren
          </Button>
        </div>
      }
    >
      {read.error ? <ProblemAlert error={read.error} /> : null}
      <QueryContent query={messages}>
        {({ items }) =>
          items.length === 0 ? (
            <EmptyNote
              icon={BellOff}
              title={unread ? 'Nichts Ungelesenes' : 'Noch keine Mitteilungen'}
            />
          ) : (
            <ul className="glass grid divide-y divide-hairline rounded-lg">
              {items.map((message) => (
                <Message
                  key={message.id}
                  message={message}
                  onRead={() => read.mutate(message.id)}
                  isReading={read.isPending && read.variables === message.id}
                />
              ))}
            </ul>
          )
        }
      </QueryContent>
    </Page>
  );
}

function Message({
  message,
  onRead,
  isReading,
}: {
  message: Notification;
  onRead: () => void;
  isReading: boolean;
}) {
  const fresh = message.readAt === null;
  const open = fresh ? onRead : undefined;
  return (
    <li className="grid gap-2 p-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
      <div className="grid min-w-0 gap-0.5">
        <span className="flex flex-wrap items-center gap-2">
          {fresh && <StatusBadge tone="info">Neu</StatusBadge>}
          <span className={fresh ? 'font-semibold' : 'font-medium text-muted'}>
            {notificationTitle[message.kind]}
          </span>
        </span>
        <span className="truncate text-sm text-muted">
          {message.caseName ? `${message.caseName} · ` : ''}
          {formatDateTime(message.createdAt)}
        </span>
      </div>
      <div className="flex flex-wrap gap-1">
        {message.caseId ? (
          <Link
            {...messageTarget(message.kind, message.caseId)}
            onClick={open}
            className={buttonClassName('secondary', 'sm')}
          >
            {opensIntake(message.kind) ? 'Aufnahme öffnen' : 'Mandant öffnen'}
          </Link>
        ) : (
          <Link to="/" onClick={open} className={buttonClassName('secondary', 'sm')}>
            Zu „Heute“
          </Link>
        )}
        {fresh && (
          <Button variant="quiet" size="sm" isPending={isReading} onPress={onRead}>
            <Check aria-hidden className="size-4" />
            Gelesen<span className="sr-only">: {notificationTitle[message.kind]}</span>
          </Button>
        )}
      </div>
    </li>
  );
}
